if (!customElements.get('arvada-product-enhancements')) {
  customElements.define('arvada-product-enhancements', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      if (!this.root || this.initialized) return;
      this.initialized = true;
      this.sectionId = this.root.dataset.section;
      this.words = JSON.parse(this.querySelector('[data-av-translations]').textContent);
      this.confirmed = this.root.dataset.avConfirmed === 'true';
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.root.addEventListener('click', this.onClick.bind(this), options);
      this.root.addEventListener('change', (event) => {
        if (event.target.closest('variant-selects')) this.root.dataset.avPending = 'true';
        if (event.target.closest('[data-av-size-field]')) {
          this.confirmed = true;
          this.root.dataset.avConfirmed = 'true';
          this.syncSize();
          const values = this.inputs().map(input => input.value);
          const direction = Math.sign(values.indexOf(event.target.value) - values.indexOf(this.lastSizeValue)) || 1;
          this.lastSizeValue = event.target.value;
          this.revealSize(event.target, true, direction);
        }
        this.syncPayment();
      }, options);
      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== this.sectionId) return;
        this.root.dataset.avPending = 'false';
        this.refresh();
      });
      this.root.addEventListener('submit', (event) => {
        if (event.target.closest('product-form')) this.syncReady();
        if (event.target.closest('product-form') && !this.checkSize()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal: this.abort.signal });
      this.root.addEventListener('click', (event) => {
        if (event.target.closest('product-form button[name="add"]') && !this.checkSize()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal: this.abort.signal });
      this.refresh();
      this.observer = new MutationObserver(() => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.refresh(), 60);
      });
      this.observer.observe(this.root.querySelector('.product__info-container'), { childList: true, subtree: true });
      this.bindModal();
      this.bindSticky();
      this.bindGallerySticky();
      this.bindGallerySwipe();
      this.bindLayout();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.stickyObserver?.disconnect();
      this.stickyResize?.disconnect();
      document.documentElement.classList.remove('av-sticky-buy-on');
      this.modalObserver?.disconnect();
      this.sizeObserver?.disconnect();
      this.layoutObserver?.disconnect();
      cancelAnimationFrame(this.layoutFrame);
      this.headerObserver?.disconnect();
      this.headerStateObserver?.disconnect();
      cancelAnimationFrame(this.sizeFrame);
      cancelAnimationFrame(this.headerFrame);
      cancelAnimationFrame(this.stickyFrame);
      this.unsubscribeVariant?.();
      clearTimeout(this.timer);
      this.initialized = false;
    }

    field() { return this.root.querySelector('[data-av-size-field]'); }
    inputs() { return Array.from(this.field()?.querySelectorAll('input[type="radio"]') || []); }
    dialog(kind) { return document.getElementById(`AV${kind}-${this.sectionId}`); }
    label(input) { return input.nextElementSibling; }

    checkSize() {
      if (this.root.dataset.avPending === 'true') return false;
      if (!this.field() || this.confirmed || this.root.dataset.avRequireSize !== 'true') return true;
      const status = this.root.querySelector('[data-av-selected-size]');
      if (status) {
        status.textContent = this.words.choose_size;
        status.setAttribute('role', 'alert');
      }
      this.field().scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.inputs()[0]?.focus();
      return false;
    }

    refresh() {
      this.observer?.disconnect();
      const field = this.field();
      if (field) {
        field.classList.add('av-size-ready');
        const inputs = this.inputs();
        this.lastSizeValue ??= inputs.find(input => input.checked)?.value;
        const isNewTrack = !field.querySelector('[data-av-size-track]')?.dataset.avBound;
        if (isNewTrack) {
          const strip = field.querySelector('.av-size-strip') || document.createElement('div');
          strip.className = 'av-size-strip';
          const track = field.querySelector('[data-av-size-track]') || document.createElement('div');
          track.className = 'av-size-track';
          track.dataset.avSizeTrack = '';
          track.dataset.avBound = 'true';
          track.setAttribute('role', 'group');
          track.setAttribute('aria-label', field.querySelector('legend span')?.textContent || this.words.selected_size);
          for (const direction of [-1, 1]) {
            const arrow = document.createElement('button');
            arrow.type = 'button';
            arrow.className = 'av-size-arrow';
            arrow.hidden = true;
            arrow.dataset.avSizeScroll = String(direction);
            arrow.textContent = direction < 0 ? '‹' : '›';
            arrow.setAttribute('aria-label', direction < 0 ? this.words.previous_sizes : this.words.next_sizes);
            strip.appendChild(arrow);
            if (direction < 0) strip.appendChild(track);
          }
          inputs.forEach(input => {
            const label = this.label(input);
            const item = document.createElement('div');
            item.className = 'av-size-item';
            item.appendChild(input);
            if (label) item.appendChild(label);
            track.appendChild(item);
          });
          field.appendChild(strip);
          this.sizeObserver?.disconnect();
          this.sizeObserver = new ResizeObserver(() => this.updateSizeArrows());
          this.sizeObserver.observe(strip);
          this.sizeObserver.observe(track);
          this.updateSizeArrows();
          track.scrollTo({ left: this.sizeScroll || 0, behavior: 'instant' });
          track.addEventListener('scroll', () => {
            if (track !== this.field()?.querySelector('[data-av-size-track]')) return;
            this.sizeScroll = track.scrollLeft;
            this.updateSizeArrows();
          }, { passive: true, signal: this.abort.signal });
          track.addEventListener('focusin', event => this.revealSize(event.target), { signal: this.abort.signal });
        }
        requestAnimationFrame(() => {
          this.updateSizeArrows();
          const selected = this.inputs().find(input => input.checked);
          if (isNewTrack && !this.hasSizePosition && this.confirmed && selected) this.revealSize(selected);
          this.hasSizePosition = true;
        });
      } else {
        this.root.dataset.avConfirmed = 'true';
      }
      this.syncSize();
      this.setupGallery();
      this.syncSticky();
      this.syncPayment();
      this.scheduleLayout?.();
      this.observer?.observe(this.root.querySelector('.product__info-container'), { childList: true, subtree: true });
    }

    bindLayout() {
      this.scheduleLayout = () => {
        cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = requestAnimationFrame(() => this.updateLayout());
      };
      this.layoutObserver = new ResizeObserver(this.scheduleLayout);
      ['.product', 'media-gallery', '.product__info-container'].forEach(selector => {
        const element = this.root.querySelector(selector);
        if (element) this.layoutObserver.observe(element);
      });
      window.addEventListener('resize', this.scheduleLayout, { passive: true, signal: this.abort.signal });
      this.scheduleLayout();
    }

    updateLayout() {
      const product = this.root.querySelector('.product');
      const gallery = this.root.querySelector('media-gallery');
      const info = this.root.querySelector('.product__info-container');
      const active = window.matchMedia('(min-width: 750px)').matches && this.root.dataset.avLayout === 'manual';
      if (!active || !product || !gallery || !info) {
        this.root.style.setProperty('--av-gallery-shift', '0px');
        this.root.style.setProperty('--av-info-shift', '0px');
        this.layoutShifts = { gallery: 0, info: 0 };
        return;
      }
      const bounds = product.getBoundingClientRect();
      const previous = this.layoutShifts || { gallery: 0, info: 0 };
      const panels = [{ key: 'gallery', element: gallery, offset: this.root.dataset.avGalleryOffset }, { key: 'info', element: info, offset: this.root.dataset.avInfoOffset }].map(panel => {
        const rect = panel.element.getBoundingClientRect();
        const base = rect.left - previous[panel.key];
        const desired = base + (Number(panel.offset) || 0) * bounds.width / 12;
        return { ...panel, base, width: rect.width, left: Math.max(bounds.left, Math.min(desired, bounds.right - rect.width)) };
      }).sort((a, b) => a.base - b.base);
      const [left, right] = panels;
      let missing = left.left + left.width + 24 - right.left;
      if (missing > 0) {
        const moveLeft = Math.min(missing / 2, left.left - bounds.left);
        left.left -= moveLeft;
        missing -= moveLeft;
        const moveRight = Math.min(missing, bounds.right - right.left - right.width);
        right.left += moveRight;
        missing -= moveRight;
        left.left -= Math.min(missing, left.left - bounds.left);
      }
      this.layoutShifts = {};
      panels.forEach(panel => {
        const shift = panel.left - panel.base;
        this.layoutShifts[panel.key] = shift;
        this.root.style.setProperty(`--av-${panel.key}-shift`, `${shift}px`);
      });
    }

    syncPayment() {
      const pending = this.root.dataset.avPending === 'true';
      const needsSize = this.field() && !this.confirmed && this.root.dataset.avRequireSize === 'true';
      const native = this.root.querySelector('product-form button[name="add"]');
      const locked = pending || Boolean(needsSize) || !native || native.disabled;
      this.root.querySelectorAll('.shopify-payment-button').forEach(payment => {
        payment.inert = locked;
        payment.setAttribute('aria-disabled', String(locked));
        payment.setAttribute('aria-busy', String(pending));
      });
    }

    syncReady() {
      let map = {};
      try { map = JSON.parse(this.root.querySelector('[data-av-ready-map]')?.textContent || '{}'); } catch (error) { map = {}; }
      const id = this.root.querySelector('product-form input[name="id"]')?.value;
      const ready = Boolean(id && map[id]);
      this.root.querySelectorAll('[data-av-ready-prop]').forEach(input => { input.disabled = !ready; });
      this.root.querySelectorAll('[data-av-trust-made]').forEach(el => { el.hidden = ready; });
      this.root.querySelectorAll('[data-av-trust-ready]').forEach(el => { el.hidden = !ready; });
      return ready;
    }

    syncSize() {
      const current = this.inputs().find(input => input.checked);
      const status = this.root.querySelector('[data-av-selected-size]');
      this.syncReady();
      if (status) {
        const text = this.confirmed && current ? `${this.words.selected_size}: ${current.value}` : this.words.choose_size;
        if (status.textContent !== text) status.textContent = text;
        status.setAttribute('role', 'status');
      }
    }

    updateSizeArrows() {
      const field = this.field(), track = field?.querySelector('[data-av-size-track]');
      if (!track) return;
      const left = field.querySelector('[data-av-size-scroll="-1"]');
      const right = field.querySelector('[data-av-size-scroll="1"]');
      const strip = track.parentElement;
      const items = Array.from(track.children);
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const needed = items.reduce((width, item) => width + item.getBoundingClientRect().width, 0) + Math.max(0, items.length - 1) * gap;
      const overflow = needed > strip.clientWidth + 1;
      strip.classList.toggle('av-size-overflow', overflow);
      left.hidden = right.hidden = !overflow;
      if (!overflow) {
        cancelAnimationFrame(this.sizeFrame);
        this.sizeAnimation = null;
        track.scrollLeft = 0;
        this.sizeScroll = 0;
      }
      left.disabled = track.scrollLeft <= 1;
      right.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    }

    scrollSizes(left) {
      const track = this.field()?.querySelector('[data-av-size-track]');
      if (!track) return;
      cancelAnimationFrame(this.sizeFrame);
      const from = track.scrollLeft;
      const target = Math.max(0, Math.min(left, track.scrollWidth - track.clientWidth));
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || Math.abs(target - from) < 1) {
        this.sizeAnimation = null;
        track.scrollLeft = target;
        this.sizeScroll = target;
        this.updateSizeArrows();
        return;
      }
      const animation = { from, target, start: performance.now() };
      this.sizeAnimation = animation;
      const step = now => {
        if (this.sizeAnimation !== animation || !this.isConnected) return;
        const currentTrack = this.field()?.querySelector('[data-av-size-track]');
        if (!currentTrack) return;
        const progress = Math.max(0, Math.min(1, (now - animation.start) / 280));
        const eased = 1 - Math.pow(1 - progress, 3);
        currentTrack.scrollLeft = animation.from + (animation.target - animation.from) * eased;
        this.sizeScroll = currentTrack.scrollLeft;
        this.updateSizeArrows();
        if (progress < 1) this.sizeFrame = requestAnimationFrame(step);
        else this.sizeAnimation = null;
      };
      this.sizeFrame = requestAnimationFrame(step);
    }

    revealSize(input, preview = false, direction = 1) {
      if (this.sizeAnimation && !preview) return;
      const track = this.field()?.querySelector('[data-av-size-track]');
      const label = input?.matches('input') ? this.label(input) : null;
      if (!track || !label) return;
      const labels = this.inputs().map(item => this.label(item));
      const index = labels.indexOf(label);
      const frame = track.getBoundingClientRect();
      const item = label.getBoundingClientRect();
      const step = item.width + 8;
      const ahead = preview ? Math.min(2, Math.max(0, Math.floor(track.clientWidth / step) - 1)) : 0;
      const next = labels[Math.min(labels.length - 1, index + ahead)].getBoundingClientRect();
      const previous = labels[Math.max(0, index - ahead)].getBoundingClientRect();
      let target = track.scrollLeft;
      if (preview && direction < 0) {
        if (previous.left < frame.left) target += previous.left - frame.left;
        else if (item.right > frame.right) target += item.right - frame.right;
      } else {
        if (item.left < frame.left) target += item.left - frame.left;
        else if (next.right > frame.right) target += next.right - frame.right;
      }
      this.scrollSizes(target);
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-av-open-guide')) this.dialog('Guide').show(button);
      if (button.hasAttribute('data-av-size-scroll')) {
        const track = this.field()?.querySelector('[data-av-size-track]');
        if (track) this.scrollSizes(track.scrollLeft + Number(button.dataset.avSizeScroll) * Math.max(56, track.clientWidth - 56));
      }
      if (button.hasAttribute('data-av-gallery-step')) this.stepGallery(Number(button.dataset.avDirection));
      if (button.hasAttribute('data-av-sticky-add')) {
        if (!this.checkSize()) return;
        const submit = this.root.querySelector('product-form button[name="add"]');
        if (submit && !submit.disabled && submit.getAttribute('aria-disabled') !== 'true') submit.click();
      }
    }

    setupGallery() {
      const gallery = this.root.querySelector('media-gallery');
      if (!gallery || gallery.dataset.avReady) return;
      gallery.dataset.avReady = 'true';
      gallery.querySelector('[id^="GalleryViewer"]')?.addEventListener('slideChanged', event => {
        if (!event.detail?.currentElement) event.stopImmediatePropagation();
      }, { capture: true, signal: this.abort.signal });
      gallery.setActiveThumbnail = function(thumbnail) {
        if (!thumbnail || !this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('button').forEach(button => button.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');
        const rail = this.elements.thumbnails.querySelector('ul');
        if (!rail) return;
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        if (mobile) rail.scrollTo({ left: thumbnail.offsetLeft - rail.offsetLeft, behavior: 'smooth' });
        else {
          const itemTop = thumbnail.offsetTop - rail.offsetTop;
          if (itemTop < rail.scrollTop) rail.scrollTo({ top: itemTop });
          else if (itemTop + thumbnail.offsetHeight > rail.scrollTop + rail.clientHeight) rail.scrollTo({ top: itemTop + thumbnail.offsetHeight - rail.clientHeight });
        }
      };
      gallery.querySelector('[id^="GalleryViewer"]')?.addEventListener('slideChanged', event => {
        if (gallery.mql.matches) return;
        const active = event.detail.currentElement;
        if (!active || active.classList.contains('is-active')) return;
        gallery.elements.viewer.querySelectorAll('[data-media-id]').forEach(slide => slide.classList.remove('is-active'));
        active.classList.add('is-active');
        gallery.playActiveMedia(active);
      }, { signal: this.abort.signal });
    }

    /* Tablet / desktop (750 px and up): swipe the main photo with a finger or pen, like on phones.
       The photo follows the finger, then the next / previous photo slides in. Mouse clicks keep opening the viewer. */
    bindGallerySwipe() {
      const gallery = this.root.querySelector('media-gallery');
      const viewer = gallery?.querySelector('[id^="GalleryViewer"]');
      const list = viewer?.querySelector('[id^="Slider-Gallery"]');
      if (!viewer || !list || viewer.dataset.avSwipe) return;
      viewer.dataset.avSwipe = 'true';
      const wide = window.matchMedia('(min-width: 750px)');
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      let start = null;
      let dx = 0;
      let dragging = false;
      let suppressClick = false;
      const slideCount = () => list.querySelectorAll(':scope > [data-media-id]:not([hidden])').length;
      const reset = (animate) => {
        list.style.transition = animate && !reduced.matches ? 'transform .25s cubic-bezier(.2,.8,.2,1)' : '';
        list.style.transform = '';
      };
      viewer.addEventListener('pointerdown', (event) => {
        if (!wide.matches || event.pointerType === 'mouse' || slideCount() < 2) return;
        if (event.target.closest('button:not(.product__modal-opener button), .av-media-arrow, deferred-media, model-viewer, video')) return;
        start = { x: event.clientX, y: event.clientY, id: event.pointerId, time: performance.now() };
        dx = 0;
        dragging = false;
      }, { passive: true, signal: this.abort.signal });
      viewer.addEventListener('pointermove', (event) => {
        if (!start || event.pointerId !== start.id) return;
        const moveX = event.clientX - start.x;
        const moveY = event.clientY - start.y;
        if (!dragging) {
          if (Math.abs(moveX) < 8 && Math.abs(moveY) < 8) return;
          if (Math.abs(moveY) > Math.abs(moveX)) { start = null; return; } // vertical: let the page scroll
          dragging = true;
          list.style.transition = '';
        }
        dx = moveX;
        list.style.transform = `translateX(${dx}px)`;
      }, { passive: true, signal: this.abort.signal });
      const finish = (event) => {
        if (!start || (event && event.pointerId !== start.id)) return;
        const wasDragging = dragging;
        const fast = Math.abs(dx) > 30 && performance.now() - start.time < 300;
        start = null;
        dragging = false;
        if (!wasDragging) return;
        suppressClick = true;
        setTimeout(() => { suppressClick = false; }, 400);
        if (Math.abs(dx) < Math.min(80, viewer.clientWidth * .18) && !fast) { reset(true); return; }
        const direction = dx < 0 ? 1 : -1;
        this.stepGallery(direction);
        if (reduced.matches) { reset(false); return; }
        // New photo comes in from the side the finger was moving away from
        list.style.transition = '';
        list.style.transform = `translateX(${direction * Math.min(viewer.clientWidth * .35, 160)}px)`;
        list.getBoundingClientRect();
        reset(true);
      };
      viewer.addEventListener('pointerup', finish, { signal: this.abort.signal });
      viewer.addEventListener('pointercancel', () => { if (start) { start = null; dragging = false; reset(true); } }, { signal: this.abort.signal });
      viewer.addEventListener('click', (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      }, { capture: true, signal: this.abort.signal });
    }

    stepGallery(direction) {
      const gallery = this.root.querySelector('media-gallery');
      const slides = Array.from(gallery.querySelectorAll('[id^="Slider-Gallery"] > [data-media-id]')).filter(slide => !slide.hidden);
      if (slides.length < 2) return;
      const current = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active')));
      gallery.setActiveMedia(slides[(current + direction + slides.length) % slides.length].dataset.mediaId, false);
    }

    bindModal() {
      const modal = document.getElementById(`ProductModal-${this.sectionId}`);
      if (!modal || modal.dataset.avBound) return;
      modal.dataset.avBound = 'true';
      modal.addEventListener('pointerup', event => {
        if (event.target.closest('[data-av-modal-step]')) event.stopPropagation();
      }, { capture: true, signal: this.abort.signal });
      modal.addEventListener('click', event => {
        const button = event.target.closest('[data-av-modal-step]');
        if (button) this.stepModal(Number(button.dataset.avDirection));
      }, { signal: this.abort.signal });
      modal.addEventListener('keydown', event => {
        if (!modal.hasAttribute('open') || !['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target.closest('video,iframe,model-viewer')) return;
        event.preventDefault();
        this.stepModal(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal: this.abort.signal });
      let touch = null;
      modal.addEventListener('touchstart', event => { touch = event.changedTouches[0].clientX; }, { passive: true, signal: this.abort.signal });
      modal.addEventListener('touchend', event => {
        if (touch === null || event.target.closest('video,iframe,model-viewer')) return;
        const delta = event.changedTouches[0].clientX - touch;
        touch = null;
        if (Math.abs(delta) > 45) this.stepModal(delta < 0 ? 1 : -1);
      }, { passive: true, signal: this.abort.signal });
      this.modalObserver = new MutationObserver(() => this.updateCounter(modal));
      this.modalObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
    }

    modalItems(modal) {
      const galleryIds = new Set(Array.from(this.root.querySelectorAll('[id^="Slider-Gallery"] > [data-media-id]')).map(node => node.dataset.mediaId));
      return Array.from(modal.querySelector('.product-media-modal__content').children).filter(node => galleryIds.has(`${this.sectionId}-${node.dataset.mediaId}`));
    }

    stepModal(direction) {
      const modal = document.getElementById(`ProductModal-${this.sectionId}`);
      const items = this.modalItems(modal);
      if (!items.length) return;
      const index = Math.max(0, items.findIndex(node => node.classList.contains('active')));
      window.pauseAllMedia();
      items.forEach(node => node.classList.remove('active'));
      const next = items[(index + direction + items.length) % items.length];
      next.classList.add('active');
      const deferred = next.matches('deferred-media') ? next : next.querySelector('deferred-media,product-model');
      deferred?.loadContent(false);
      const gallery = this.root.querySelector('media-gallery');
      gallery?.setActiveMedia(`${this.sectionId}-${next.dataset.mediaId}`, false);
      this.updateCounter(modal);
    }

    updateCounter(modal) {
      const items = this.modalItems(modal);
      const index = items.findIndex(node => node.classList.contains('active'));
      const counter = modal.querySelector('[data-av-modal-counter]');
      if (counter) counter.textContent = `${Math.max(0, index) + 1} / ${items.length}`;
    }

    bindGallerySticky() {
      this.headerElements = Array.from(document.querySelectorAll('.section-header, .announcement-bar-section'));
      const update = () => {
        let reserved = 16;
        let visible = 16;
        this.headerElements.forEach(element => {
          const style = getComputedStyle(element);
          const stickyType = element.querySelector('sticky-header')?.getAttribute('data-sticky-type');
          const rect = element.getBoundingClientRect();
          reserved += rect.height;
          if (!['sticky', 'fixed'].includes(style.position) && (!stickyType || stickyType === 'none')) return;
          if (style.visibility !== 'hidden' && rect.bottom > 0 && rect.top < window.innerHeight) {
            visible = Math.max(visible, rect.bottom + 16);
          }
        });
        for (const [property, value] of [['--av-header-space', reserved], ['--av-sticky-top', visible]]) {
          const pixels = `${Math.ceil(value)}px`;
          if (this.root.style.getPropertyValue(property) !== pixels) this.root.style.setProperty(property, pixels);
        }
        if (performance.now() < this.headerTransitionEnd) this.headerFrame = requestAnimationFrame(update);
        else this.headerFrame = null;
      };
      const schedule = () => {
        if (this.headerFrame == null) this.headerFrame = requestAnimationFrame(update);
      };
      this.headerObserver = new ResizeObserver(schedule);
      this.headerStateObserver = new MutationObserver(() => {
        this.headerTransitionEnd = performance.now() + 250;
        schedule();
      });
      this.headerElements.forEach(element => {
        this.headerObserver.observe(element);
        this.headerStateObserver.observe(element, { attributes: true, attributeFilter: ['class', 'style'] });
      });
      window.addEventListener('resize', schedule, { passive: true, signal: this.abort.signal });
      window.addEventListener('scroll', schedule, { passive: true, signal: this.abort.signal });
      schedule();
    }

    bindSticky() {
      const target = this.root.querySelector('product-form button[name="add"]');
      const sticky = this.root.querySelector('[data-av-sticky]');
      if (!target || !sticky || !('IntersectionObserver' in window)) return;
      /* Mobile: while the fixed “Add to cart” bar is visible, the footer gets exactly that much extra room at the bottom
         (product page only — nothing is added to other pages), so policies and the last links can be scrolled into view. */
      const root = document.documentElement;
      const update = () => {
        sticky.hidden = target.getBoundingClientRect().bottom > 0;
        const height = window.matchMedia('(max-width: 749px)').matches && !sticky.hidden ? Math.ceil(sticky.getBoundingClientRect().height) : 0;
        root.style.setProperty('--av-sticky-buy-h', `${height}px`);
        root.classList.toggle('av-sticky-buy-on', height > 0);
      };
      this.stickyResize = new ResizeObserver(update);
      this.stickyResize.observe(sticky);
      const schedule = () => {
        cancelAnimationFrame(this.stickyFrame);
        this.stickyFrame = requestAnimationFrame(update);
      };
      this.stickyObserver = new IntersectionObserver(update);
      this.stickyObserver.observe(target);
      window.addEventListener('scroll', schedule, { passive: true, signal: this.abort.signal });
      window.addEventListener('resize', schedule, { passive: true, signal: this.abort.signal });
      update();
    }

    syncSticky() {
      const sticky = this.root.querySelector('[data-av-sticky]');
      if (!sticky) return;
      const price = this.root.querySelector('.price .price-item--sale') || this.root.querySelector('.price .price-item--regular');
      const label = sticky.querySelector('[data-av-sticky-label]');
      const selected = this.inputs().find(input => input.checked);
      label.textContent = [price?.textContent.trim(), this.confirmed ? selected?.value : this.words.choose_size].filter(Boolean).join(' · ');
      const native = this.root.querySelector('product-form button[name="add"]');
      const button = sticky.querySelector('button');
      button.disabled = !native || native.disabled || native.getAttribute('aria-disabled') === 'true';
    }
  });
}

if (!customElements.get('arvada-details-preview')) {
  customElements.define('arvada-details-preview', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.content = this.querySelector('[data-av-details-content]');
      this.button = this.querySelector('[data-av-details-toggle]');
      this.expanded = false;
      const marker = this.findMarker();
      if (this.dataset.mode === 'marker' && marker) {
        const before = document.createRange();
        before.selectNodeContents(this.content);
        before.setEndBefore(marker.start);
        const after = document.createRange();
        after.selectNodeContents(this.content);
        after.setStartAfter(marker.end);
        const preview = before.cloneContents();
        this.remainder = document.createElement('div');
        this.remainder.className = 'av-details-remainder';
        this.remainder.append(after.cloneContents());
        this.content.replaceChildren(preview, this.remainder);
        this.removeMarkers(this.content);
        this.canExpand = this.remainder.textContent.trim().length > 0 || Boolean(this.remainder.querySelector('img,video,table'));
      } else {
        this.removeMarkers(this.content);
      }
      this.formatColumns();
      this.button.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.update();
      }, { signal: this.abort.signal });
      this.closest('details')?.addEventListener('toggle', () => this.measure(), { signal: this.abort.signal });
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.content);
      document.fonts?.ready.then(() => { if (this.isConnected) this.measure(); });
      this.measure();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.initialized = false;
    }

    formatColumns() {
      const blocks = [...this.content.querySelectorAll('p,li')].filter(block => !block.querySelector('p,ul,ol,table'));
      for (const block of blocks) {
        if (block.querySelector('.av-spec-row') || !block.textContent.includes('|')) continue;
        const breaks = [...block.querySelectorAll('br')];
        const fragments = [];
        let previous = null;
        for (const boundary of [...breaks, null]) {
          const range = document.createRange();
          range.selectNodeContents(block);
          if (previous) range.setStartAfter(previous);
          if (boundary) range.setEndBefore(boundary);
          fragments.push(range.cloneContents());
          previous = boundary;
        }
        const result = document.createDocumentFragment();
        let previousRow = false;
        fragments.forEach((fragment, index) => {
          const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT);
          let separator = null;
          let node;
          while ((node = walker.nextNode())) {
            const offset = node.data.indexOf('|');
            if (offset !== -1) { separator = { node, offset }; break; }
          }
          let row = null;
          if (separator) {
            const left = document.createRange();
            left.selectNodeContents(fragment);
            left.setEnd(separator.node, separator.offset);
            const right = document.createRange();
            right.selectNodeContents(fragment);
            right.setStart(separator.node, separator.offset + 1);
            const label = left.cloneContents();
            const value = right.cloneContents();
            if (label.textContent.trim() && value.textContent.trim()) {
              row = document.createElement('span');
              row.className = 'av-spec-row';
              for (const [content, className] of [[label, 'av-spec-label'], [value, 'av-spec-value']]) {
                const cell = document.createElement('span');
                cell.className = className;
                cell.append(content);
                row.append(cell);
              }
            }
          }
          if (index && (!row || !previousRow)) result.append(document.createElement('br'));
          result.append(row || fragment);
          previousRow = Boolean(row);
        });
        block.replaceChildren(result);
      }
    }

    findMarker() {
      const sentinel = this.content.querySelector('[data-av-details-marker]');
      const candidates = [];
      if (sentinel) candidates.push({ start: sentinel, end: sentinel });
      for (const element of this.content.querySelectorAll('p,li,blockquote')) {
        if (/^_{10,}$/.test(element.textContent.trim())) candidates.push({ start: element, end: element });
      }
      const walker = document.createTreeWalker(this.content, NodeFilter.SHOW_TEXT);
      let text;
      while ((text = walker.nextNode())) {
        if (!/^_{10,}$/.test(text.textContent.trim())) continue;
        let line = text;
        while (line.parentElement && line.parentElement !== this.content && /^(STRONG|EM|SPAN|B|I|U)$/.test(line.parentElement.tagName)) line = line.parentElement;
        const previous = line.previousSibling;
        const next = line.nextSibling;
        if ((!previous || previous.nodeName === 'BR') && (!next || next.nodeName === 'BR')) {
          candidates.push({ start: line, end: next?.nodeName === 'BR' ? next : line });
        }
      }
      candidates.sort((a, b) => a.start.compareDocumentPosition(b.start) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
      return candidates[0];
    }

    removeMarkers(container) {
      const original = this.content;
      this.content = container;
      let marker;
      while ((marker = this.findMarker())) {
        const range = document.createRange();
        range.setStartBefore(marker.start);
        range.setEndAfter(marker.end);
        range.deleteContents();
      }
      this.content = original;
    }

    measure() {
      if (!this.content.getBoundingClientRect().width) return;
      if (this.dataset.mode === 'lines') {
        const style = getComputedStyle(this.content);
        const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.6;
        const walker = document.createTreeWalker(this.content, NodeFilter.SHOW_TEXT);
        const rects = [];
        let text;
        while ((text = walker.nextNode())) {
          if (!text.textContent.trim() || text.parentElement.closest('[hidden],script,style')) continue;
          const range = document.createRange();
          range.selectNodeContents(text);
          rects.push(...Array.from(range.getClientRects()).filter(rect => rect.width && rect.height));
        }
        rects.sort((a, b) => a.top - b.top || a.left - b.left);
        const lines = [];
        rects.forEach(rect => {
          const line = lines[lines.length - 1];
          if (line && rect.top < line.bottom - 1 && rect.bottom > line.top + 1) {
            line.top = Math.min(line.top, rect.top);
            line.bottom = Math.max(line.bottom, rect.bottom);
          } else lines.push({ top: rect.top, bottom: rect.bottom });
        });
        const count = Number(this.dataset.lines || 6);
        const last = lines[Math.min(count, lines.length) - 1];
        this.previewHeight = last ? last.bottom - this.content.getBoundingClientRect().top + Math.max(0, (lineHeight - (last.bottom - last.top)) / 2) : 0;
        this.content.style.setProperty('--av-preview-height', `${this.previewHeight}px`);
        const top = this.content.getBoundingClientRect().top;
        this.content.querySelectorAll('.av-spec-row').forEach(row => {
          const rect = row.getBoundingClientRect();
          if (rect.top < top + this.previewHeight && rect.bottom > top + this.previewHeight) this.previewHeight = rect.bottom - top;
        });
        this.content.style.setProperty('--av-preview-height', `${this.previewHeight}px`);
        this.canExpand = lines.some(line => line.top >= top + this.previewHeight - 1);
      }
      this.update();
    }

    update() {
      const collapsed = Boolean(this.canExpand) && !this.expanded;
      this.content.dataset.clipped = String(this.dataset.mode === 'lines' && collapsed);
      if (this.remainder) this.remainder.hidden = collapsed;
      this.button.hidden = !this.canExpand;
      this.button.setAttribute('aria-expanded', String(!collapsed));
      this.button.querySelector('[data-av-more]').hidden = !collapsed;
      this.button.querySelector('[data-av-less]').hidden = collapsed;
      if (this.dataset.mode === 'lines') {
        const boundary = this.content.getBoundingClientRect().top + this.previewHeight;
        this.content.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(element => {
          if (collapsed && element.getBoundingClientRect().bottom > boundary + 1) {
            if (!element.hasAttribute('data-av-saved-tabindex')) element.dataset.avSavedTabindex = element.getAttribute('tabindex') ?? '';
            element.setAttribute('tabindex', '-1');
          } else if (element.hasAttribute('data-av-saved-tabindex')) {
            const saved = element.dataset.avSavedTabindex;
            if (saved) element.setAttribute('tabindex', saved);
            else element.removeAttribute('tabindex');
            delete element.dataset.avSavedTabindex;
          }
        });
      }
    }
  });
}

/* Collapsible rows (Materials, Warranty, …): smooth open / close — about 0.2 s, fast start then a soft stop (ease-out),
   so the photo next to it glides instead of jumping. Reduced-motion users get the instant toggle. */
(() => {
  if (window.avAccordionMotion) return;
  window.avAccordionMotion = true;
  const DURATION = 240;
  // Quick start, long soft landing (ease-out) — no hard stop at the end
  const EASING = 'cubic-bezier(0.25, 0.8, 0.35, 1)';
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('.product__accordion details > summary');
    if (!summary || !summary.closest('.arvada-product')) return;
    const details = summary.parentElement;
    if (!details.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    event.preventDefault();
    const opening = !details.open || details.dataset.avClosing === 'true';
    // Continue from wherever a running animation is right now (fast double clicks do not jump)
    const start = details.getBoundingClientRect().height;
    details.avAnimation?.cancel();
    details.avFade?.cancel();
    // Measure the final height (no paint happens in between)
    details.open = !opening ? false : true;
    const end = details.getBoundingClientRect().height;
    details.open = true;
    details.dataset.avClosing = String(!opening);
    summary.setAttribute('aria-expanded', String(opening));
    details.style.overflow = 'hidden';
    const animation = details.animate({ height: [`${start}px`, `${end}px`] }, { duration: DURATION, easing: EASING });
    details.avAnimation = animation;
    const content = details.querySelector(':scope > :not(summary)');
    details.avFade = content?.animate({ opacity: opening ? [0, 1] : [1, 0] }, { duration: DURATION, easing: 'linear', fill: 'forwards' });
    animation.onfinish = () => {
      if (!opening) details.open = false;
      delete details.dataset.avClosing;
      details.style.overflow = '';
      details.avAnimation = null;
      details.avFade?.cancel();
      details.avFade = null;
    };
  });
})();
